import { format, parseISO } from 'date-fns'
import { AlertCircle, FilePenLine } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'

import { ApplicationStatusBadge } from '@/features/admin/applications/components/ApplicationStatusBadge'
import { useLatestApplication } from '@/features/membership/hooks/useLatestApplication'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { ApplicationResponseDTO, ApplicationResponseDTOStatus } from '@/utils/api/membership-service/models'
import { ApplicationResponseDTOStatus as ApplicationStatus } from '@/utils/api/membership-service/models'

function resolveApplicationStatus(status?: string | null): ApplicationStatus {
  switch (status) {
    case ApplicationStatus.APPROVED:
    case ApplicationStatus.PENDING:
    case ApplicationStatus.REJECTED:
    case ApplicationStatus.RENEWED:
      return status
    default:
      return ApplicationStatus.PENDING
  }
}

function resolveReferenceDate(application: ApplicationResponseDTO): number {
  const reference = application.updatedAt ?? application.submittedAt ?? application.createdAt ?? null
  if (!reference) return 0
  return new Date(reference).getTime()
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  try {
    return format(parseISO(value), 'PPP p')
  } catch {
    return '—'
  }
}

function canEditStatus(status?: ApplicationResponseDTOStatus): boolean {
  return status !== ApplicationStatus.APPROVED && status !== ApplicationStatus.RENEWED
}

function MembershipApplicationsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`application-skeleton-${index}`} className="space-y-2 rounded-lg border p-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64 max-w-full" />
              <Skeleton className="h-9 w-36" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

export function MembershipApplicationsPage() {
  const { applications, query } = useLatestApplication({ pageSize: 200 })

  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => resolveReferenceDate(b) - resolveReferenceDate(a))
  }, [applications])

  const isInitialLoading = query.isPending && !query.data

  if (isInitialLoading) {
    return <MembershipApplicationsSkeleton />
  }

  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to load applications</AlertTitle>
        <AlertDescription>
          {query.error instanceof Error
            ? query.error.message
            : 'We could not load your membership applications right now.'}
        </AlertDescription>
      </Alert>
    )
  }

  if (sortedApplications.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My applications</h1>
          <p className="text-sm text-muted-foreground">
            Open this page any time to edit application details when the review team requests corrections.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>No applications yet</CardTitle>
            <CardDescription>
              Submit a membership application first. You will then be able to revise it here when edits are requested.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/membership/apply">Start membership application</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My applications</h1>
        <p className="text-sm text-muted-foreground">
          Select an application below to edit it. Approved and renewed applications are read-only.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Membership applications</CardTitle>
          <CardDescription>
            You can update pending or rejected applications and resubmit as instructed by reviewers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {sortedApplications.map((application) => {
            const status = resolveApplicationStatus(application.status)
            const canEdit = Boolean(application.id) && canEditStatus(status)
            const submittedAt = formatDateTime(application.submittedAt ?? application.createdAt)
            const lastUpdatedAt = formatDateTime(application.updatedAt ?? application.submittedAt ?? application.createdAt)

            return (
              <div key={application.id ?? `${application.categoryName}-${submittedAt}`} className="space-y-4 rounded-xl border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">
                      {application.categoryName || 'Membership application'}
                      {application.id ? ` #${application.id}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Submitted: {submittedAt} | Last updated: {lastUpdatedAt}
                    </p>
                  </div>
                  <ApplicationStatusBadge status={status} />
                </div>

                {application.rejectionReason ? (
                  <Alert variant="destructive" className="py-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Reviewer note</AlertTitle>
                    <AlertDescription>{application.rejectionReason}</AlertDescription>
                  </Alert>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    {canEdit
                      ? 'This application can be edited.'
                      : 'This application is not editable in its current status.'}
                  </p>
                  {canEdit ? (
                    <Button asChild size="sm">
                      <Link
                        to="/membership/profile"
                        search={{ applicationId: application.id as number }}
                      >
                        <FilePenLine className="mr-2 h-4 w-4" />
                        Edit application
                      </Link>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled>
                      <FilePenLine className="mr-2 h-4 w-4" />
                      Read-only
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}
